import { useEffect, useState } from 'react'
import {
  Popover, ActionIcon, Stack, Group, Text, NumberInput, Button, Badge, Tooltip, Skeleton,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconCurrencyDollar } from '@tabler/icons-react'
import { useTenantBudgetStatus, useTenantBudgetSave } from '@/hooks/useTenantBudget'

interface Props {
  tenantId: number
  tenantName: string
}

function colorForStatus(s: { has_threshold?: boolean; exceeded?: boolean; alert?: boolean } | undefined): string {
  if (!s || !s.has_threshold) return 'gray'
  if (s.exceeded) return 'red'
  if (s.alert) return 'yellow'
  return 'teal'
}

export function TenantBudgetIcon({ tenantId, tenantName }: Props) {
  const [open, setOpen] = useState(false)
  const { data, isLoading } = useTenantBudgetStatus(tenantId, { enabled: open })
  const save = useTenantBudgetSave(tenantId)
  const [threshold, setThreshold] = useState<number | string>(1000)
  const [alertPct, setAlertPct] = useState<number | string>(80)

  useEffect(() => {
    if (data?.threshold_usd != null) setThreshold(data.threshold_usd)
    if (data?.alert_at_pct != null) setAlertPct(data.alert_at_pct)
  }, [data])

  const iconColor = colorForStatus(data)
  const tooltipLabel = data?.has_threshold
    ? `Budget: $${data.threshold_usd} · ${data.exceeded ? 'excedido' : data.alert ? 'em alerta' : 'normal'}`
    : 'Budget: sem threshold definido'

  const handleSave = () => {
    save.mutate(
      { threshold_usd: Number(threshold), alert_at_pct: Number(alertPct) },
      {
        onSuccess: () => {
          notifications.show({ color: 'teal', title: 'Salvo', message: `Budget de ${tenantName} atualizado.` })
          setOpen(false)
        },
        onError: () => {
          notifications.show({ color: 'red', title: 'Erro', message: 'Falha ao salvar budget.' })
        },
      },
    )
  }

  return (
    <Popover opened={open} onChange={setOpen} position="bottom-end" withArrow shadow="md" width={280}>
      <Popover.Target>
        <Tooltip label={tooltipLabel} withArrow>
          <ActionIcon
            variant="subtle"
            color={iconColor}
            size="md"
            aria-label={`Budget ${tenantName}`}
            onClick={() => setOpen((v) => !v)}
          >
            <IconCurrencyDollar size={16} />
          </ActionIcon>
        </Tooltip>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap="xs">
          <Group justify="space-between">
            <Text fw={600} size="sm">Budget · {tenantName}</Text>
            {data?.has_threshold && (
              <Badge size="xs" color={iconColor} variant="light">
                {data.exceeded ? 'Excedido' : data.alert ? 'Em Alerta' : 'Normal'}
              </Badge>
            )}
          </Group>
          {isLoading ? (
            <Skeleton height={80} />
          ) : (
            <>
              <NumberInput
                label="Threshold mensal (USD)"
                value={threshold}
                onChange={setThreshold}
                prefix="$"
                min={1}
                decimalScale={2}
              />
              <NumberInput
                label="Alerta em %"
                value={alertPct}
                onChange={setAlertPct}
                suffix="%"
                min={1}
                max={100}
              />
              <Group justify="flex-end" gap="xs">
                <Button variant="subtle" size="xs" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button size="xs" loading={save.isPending} onClick={handleSave}>Salvar</Button>
              </Group>
            </>
          )}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  )
}
